import { Injectable } from '@angular/core';
import { catchError } from 'rxjs/internal/operators';
import { Observable, BehaviorSubject } from 'rxjs';
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { RuntimeConfigLoaderService } from 'runtime-config-loader';
import { GlobalSearchService } from '../services/globalsearchservice.service';
import { CRMTypes, LeadStatus, OpportunityStage, ActivityType, LeadSource, LostReason, Opportunity, Activity } from '../classes/crm';

@Injectable({
    providedIn: 'root'
})
export class CrmService {
    key = 'UET6DFMPAXW7BKCB';
    config: any;
    user: any = [];
    private crmTypesLoaded = false;

    constructor(private http: HttpClient, private _configSvc: RuntimeConfigLoaderService, private globalSearchService: GlobalSearchService) {
        this.globalSearchService.configsubscription.subscribe(r => {
            this.config = r;
            this.updateHeaders();
            this.loadCRMTypesIfNeeded();
        });

        this.globalSearchService.user.subscribe((result) => {
            this.user = result;
            this.updateHeaders();
            this.loadCRMTypesIfNeeded();
        });

        this.updateHeaders();
    }

    public loadCRMTypesIfNeeded() {
        if (this.config && this.user && !this.crmTypesLoaded) {
            this.crmTypesLoaded = true;
            this.getCRMTypes().subscribe({
                next: (crmTypes) => {
                    // Update GlobalSearchService BehaviorSubjects
                    this.globalSearchService.crm_types.next(crmTypes);
                    this.globalSearchService.lead_statuses.next(crmTypes.leadstatuses || []);
                    this.globalSearchService.opportunity_stages.next(crmTypes.opportunitystages || []);
                    this.globalSearchService.activity_types.next(crmTypes.activitytypes || []);
                    this.globalSearchService.lead_sources.next(crmTypes.leadsources || []);
                    this.globalSearchService.lost_reasons.next(crmTypes.lostreasons || []);
                },
                error: (error) => {
                    console.error('Failed to load CRM types:', error);
                    this.crmTypesLoaded = false; // Allow retry
                }
            });
        }
    }

    public httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
        })
    };

    public getOptions = {
        headers: new HttpHeaders({})
    };

    private updateHeaders() {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        if (this.config && this.config.apiCredentials && this.config.apiCredentials.clientId && this.user && this.user.user && this.user.user.userid) {
            headers = headers.set('Authorization', 'Bearer ' + this.config.apiCredentials.clientId + '_' + this.user.user.userid);
        }
        this.httpOptions.headers = headers;
        this.getOptions.headers = headers;
    }

    private setEndPoint(method: string): string {
        return this.config.apiServer.endpoint + method + this.getKey();
    }

    public getKey(): string {
        return '&apikey=' + this.key;
    }

    private handleError<T>(operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
                window.location.href = '/#/auth/logout';
            }
            return error;
        };
    }

    // ====================
    // CRM Types Management (System Configuration)
    // ====================

    public getCRMTypes(): Observable<CRMTypes> {
        const method = 'crm/types';
        return this.http.get<CRMTypes>(this.setEndPoint(method), this.getOptions);
    }

    // Lead Statuses
    public getLeadStatuses(): Observable<LeadStatus[]> {
        const method = 'crm/types/leadstatuses';
        return this.http.get<LeadStatus[]>(this.setEndPoint(method), this.getOptions);
    }

    public createLeadStatus(data: any): Observable<LeadStatus> {
        const method = 'crm/types/leadstatuses';
        return this.http.post<LeadStatus>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public updateLeadStatus(id: number, data: any): Observable<LeadStatus> {
        const method = `crm/types/leadstatuses/${id}`;
        return this.http.put<LeadStatus>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public deleteLeadStatus(id: number): Observable<any> {
        const method = `crm/types/leadstatuses/${id}`;
        return this.http.delete(this.setEndPoint(method), this.httpOptions).pipe(
            catchError(this.handleError(method, null))
        );
    }

    // Opportunity Stages
    public getOpportunityStages(): Observable<OpportunityStage[]> {
        const method = 'crm/types/opportunitystages';
        return this.http.get<OpportunityStage[]>(this.setEndPoint(method), this.getOptions);
    }

    public createOpportunityStage(data: any): Observable<OpportunityStage> {
        const method = 'crm/types/opportunitystages';
        return this.http.post<OpportunityStage>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public updateOpportunityStageType(id: number, data: any): Observable<OpportunityStage> {
        const method = `crm/types/opportunitystages/${id}`;
        return this.http.put<OpportunityStage>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public deleteOpportunityStage(id: number): Observable<any> {
        const method = `crm/types/opportunitystages/${id}`;
        return this.http.delete(this.setEndPoint(method), this.httpOptions).pipe(
            catchError(this.handleError(method, null))
        );
    }

    // Activity Types
    public getActivityTypes(): Observable<ActivityType[]> {
        const method = 'crm/types/activitytypes';
        return this.http.get<ActivityType[]>(this.setEndPoint(method), this.getOptions);
    }

    public createActivityType(data: any): Observable<ActivityType> {
        const method = 'crm/types/activitytypes';
        return this.http.post<ActivityType>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public updateActivityType(id: number, data: any): Observable<ActivityType> {
        const method = `crm/types/activitytypes/${id}`;
        return this.http.put<ActivityType>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public deleteActivityType(id: number): Observable<any> {
        const method = `crm/types/activitytypes/${id}`;
        return this.http.delete(this.setEndPoint(method), this.httpOptions).pipe(
            catchError(this.handleError(method, null))
        );
    }

    // Lead Sources
    public getLeadSources(): Observable<LeadSource[]> {
        const method = 'crm/types/leadsources';
        return this.http.get<LeadSource[]>(this.setEndPoint(method), this.getOptions);
    }

    public createLeadSource(data: any): Observable<LeadSource> {
        const method = 'crm/types/leadsources';
        return this.http.post<LeadSource>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public updateLeadSource(id: number, data: any): Observable<LeadSource> {
        const method = `crm/types/leadsources/${id}`;
        return this.http.put<LeadSource>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public deleteLeadSource(id: number): Observable<any> {
        const method = `crm/types/leadsources/${id}`;
        return this.http.delete(this.setEndPoint(method), this.httpOptions).pipe(
            catchError(this.handleError(method, null))
        );
    }

    // Lost Reasons
    public getLostReasons(): Observable<LostReason[]> {
        const method = 'crm/types/lostreasons';
        return this.http.get<LostReason[]>(this.setEndPoint(method), this.getOptions);
    }

    public createLostReason(data: any): Observable<LostReason> {
        const method = 'crm/types/lostreasons';
        return this.http.post<LostReason>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public updateLostReason(id: number, data: any): Observable<LostReason> {
        const method = `crm/types/lostreasons/${id}`;
        return this.http.put<LostReason>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public deleteLostReason(id: number): Observable<any> {
        const method = `crm/types/lostreasons/${id}`;
        return this.http.delete(this.setEndPoint(method), this.httpOptions).pipe(
            catchError(this.handleError(method, null))
        );
    }

    // ====================
    // CRM Data Operations
    // ====================

    public getOpportunities(filters?: any): Observable<Opportunity[]> {
        let method = 'crm/opportunities';
        if (filters) {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key] != null) params.append(key, filters[key]);
            });
            if (params.toString()) method += '?' + params.toString();
        }
        return this.http.get<Opportunity[]>(this.setEndPoint(method), this.getOptions);
    }

    public getOpportunity(id: number): Observable<Opportunity> {
        const method = `crm/opportunities/${id}`;
        return this.http.get<Opportunity>(this.setEndPoint(method), this.getOptions);
    }

    public createOpportunity(data: any): Observable<Opportunity> {
        const method = 'crm/opportunities';
        return this.http.post<Opportunity>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public updateOpportunity(id: number, data: any): Observable<Opportunity> {
        const method = `crm/opportunities/${id}`;
        return this.http.put<Opportunity>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public deleteOpportunity(id: number): Observable<any> {
        const method = `crm/opportunities/${id}`;
        return this.http.delete(this.setEndPoint(method), this.httpOptions).pipe(
            catchError(this.handleError(method, null))
        );
    }

    public updateOpportunityStage(id: number, stageId: number): Observable<Opportunity> {
        const method = `crm/opportunities/${id}/stage`;
        return this.http.put<Opportunity>(this.setEndPoint(method), { stage_id: stageId }, this.httpOptions).pipe(
            catchError(this.handleError<Opportunity>(method))
        );
    }

    public updateOpportunityStageForOpportunity(id: number, stage_id: number): Observable<Opportunity> {
        // Alias for backwards compatibility
        return this.updateOpportunityStage(id, stage_id);
    }

    public getOpportunitiesByCustomer(debtorno: string, branchcode?: string): Observable<Opportunity[]> {
        let method = `crm/opportunities/customer/${debtorno}`;
        if (branchcode) {
            method += `?branchcode=${branchcode}`;
        }
        return this.http.get<Opportunity[]>(this.setEndPoint(method), this.getOptions);
    }

    public getActivities(filters?: any): Observable<Activity[]> {
        let method = 'crm/activities';
        if (filters) {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key] != null) params.append(key, filters[key]);
            });
            if (params.toString()) method += '?' + params.toString();
        }
        return this.http.get<Activity[]>(this.setEndPoint(method), this.getOptions);
    }

    public createActivity(data: any): Observable<Activity> {
        const method = 'crm/activities';
        return this.http.post<Activity>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    public updateActivity(id: number, data: any): Observable<Activity> {
        const method = `crm/activities/${id}`;
        return this.http.put<Activity>(this.setEndPoint(method), data, this.httpOptions).pipe(
            catchError(this.handleError(method, data))
        );
    }

    // Helper methods for looking up from cached types
    public getStageColor(stage_id: number): string {
        const stages = this.globalSearchService.opportunity_stages.getValue();
        if (stages) {
            return stages.find(s => s.id === stage_id)?.color || '#6c757d'; // Default to grey
        }
        return '#6c757d';
    }

    public getLeadStatusName(status_id: number): string {
        const statuses = this.globalSearchService.lead_statuses.getValue();
        if (statuses) {
            return statuses.find(s => s.id === status_id)?.name || 'N/A';
        }
        return 'N/A';
    }

    public getLeadStatusColor(status_id: number): string {
        const statuses = this.globalSearchService.lead_statuses.getValue();
        if (statuses) {
            return statuses.find(s => s.id === status_id)?.color || '#6c757d';
        }
        return '#6c757d';
    }

    public getActivityTypeName(type_id: number): string {
        const types = this.globalSearchService.activity_types.getValue();
        if (types) {
            return types.find(t => t.id === type_id)?.name || 'N/A';
        }
        return 'N/A';
    }

    // ====================
    // Enhanced Activities Management
    // ====================

    public getActivitiesByCustomer(debtorno: string, branchcode?: string): Observable<Activity[]> {
        let method = `crm/activities/customer/${debtorno}`;
        if (branchcode) method += '?branchcode=' + branchcode;
        return this.http.get<Activity[]>(this.setEndPoint(method), this.getOptions);
    }

    public deleteActivity(id: number): Observable<any> {
        const method = `crm/activities/${id}`;
        return this.http.delete(this.setEndPoint(method), this.httpOptions).pipe(
            catchError(this.handleError(method, null))
        );
    }

    public getUpcomingFollowUps(days: number = 7): Observable<Activity[]> {
        const method = `crm/activities/followups?days=${days}`;
        return this.http.get<Activity[]>(this.setEndPoint(method), this.getOptions);
    }

    // ====================
    // Dashboard Statistics
    // ====================

    public getLeadStats(): Observable<any> {
        const method = 'crm/stats/leads';
        return this.http.get(this.setEndPoint(method), this.getOptions);
    }

    public getOpportunityStats(): Observable<any> {
        const method = 'crm/stats/opportunities';
        return this.http.get(this.setEndPoint(method), this.getOptions);
    }

    public getActivityStats(): Observable<any> {
        const method = 'crm/stats/activities';
        return this.http.get(this.setEndPoint(method), this.getOptions);
    }
}
